import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { CreditCard, CheckCircle2, Wifi } from 'lucide-react';
import './CardPaymentModal.css';

const CardPaymentModal = ({ show, onHide, onConfirm, amount, currencySymbol, customerPoints = 0, rewardSettings = null, prefilledPoints = "" }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [maskedCard, setMaskedCard] = useState("");

  const calculatePointsDiscount = () => {
    const points = parseFloat(pointsToRedeem) || 0;
    const rate = rewardSettings?.point_conversion_rate || rewardSettings?.conversion_rate || 1;
    return points * parseFloat(rate);
  };

  const finalAmount = Math.max(0, Number(amount) - calculatePointsDiscount());

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setIsProcessing(false);
      setIsSuccess(false);
      setPointsToRedeem(prefilledPoints);
      setTransactionCode("");
      setMaskedCard("");
    }
  }, [show]);

  const handleSimulateSwipe = () => {
    if (isProcessing) return;

    setIsProcessing(true);

    // Generate a random masked card like a real terminal would show
    const lastFour = Math.floor(1000 + Math.random() * 9000).toString();
    const simulatedMasked = `**** **** **** ${lastFour}`;
    setMaskedCard(simulatedMasked);

    // Generate a realistic transaction code
    const part1 = Math.random().toString(36).substring(2, 6).toUpperCase();
    const part2 = Math.random().toString(36).substring(2, 6).toUpperCase();
    const generatedCode = `TXN-${part1}-${part2}`;
    setTransactionCode(generatedCode);

    // Simulate card reader processing delay
    setTimeout(() => {
      setIsProcessing(false);
      setIsSuccess(true);

      // Auto-confirm after showing the transaction code
      setTimeout(() => {
        const rPoints = parseFloat(pointsToRedeem) || 0;
        const rAmount = calculatePointsDiscount();
        onConfirm(rPoints, rAmount, generatedCode, simulatedMasked);
      }, 3000);
    }, 2000);
  };

  return (
    <Modal show={show} onHide={isProcessing || isSuccess ? null : onHide} centered className="card-payment-modal custom-modal-theme">
      <Modal.Header closeButton={!isProcessing && !isSuccess} className="modal-header-glass border-0 pb-0">
        <Modal.Title style={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CreditCard color="#604BE8" /> Card Payment
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {isSuccess ? (
          /* ── SUCCESS SCREEN ── */
          <div className="d-flex flex-column align-items-center justify-content-center py-4">
            <CheckCircle2 size={72} color="#10b981" className="mb-3 payment-success-icon" />
            <h4 style={{ color: '#10b981', fontWeight: '800', marginBottom: '4px' }}>Payment Approved!</h4>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>Card transaction successful</p>

            {/* Transaction Code Box */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              padding: '18px 32px',
              borderRadius: '14px',
              border: '1.5px dashed #94a3b8',
              textAlign: 'center',
              width: '100%',
              maxWidth: '320px',
              marginBottom: '14px'
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Approval Code</p>
              <p style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '900', color: '#1e293b', letterSpacing: '2px', fontFamily: 'monospace' }}>{transactionCode}</p>
              <p style={{ margin: '0 0 2px 0', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Card</p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#475569', fontFamily: 'monospace' }}>{maskedCard}</p>
            </div>

            {/* Amount */}
            <div style={{ background: '#f0fdf4', padding: '10px 24px', borderRadius: '10px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
              <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#16a34a', fontWeight: '600', textTransform: 'uppercase' }}>Amount Charged</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: '900', color: '#15803d' }}>
                {currencySymbol} {finalAmount.toFixed(2)}
              </p>
            </div>

            <p style={{ marginTop: '16px', fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>
              ⏳ Processing your order...
            </p>
          </div>
        ) : (
          /* ── SWIPE SCREEN ── */
          <>
            {/* Card Terminal Visual */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              {/* Animated Card Reader */}
              <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                borderRadius: '16px',
                padding: '28px 24px 24px',
                maxWidth: '300px',
                margin: '0 auto',
                boxShadow: '0 10px 30px rgba(30, 41, 59, 0.3)',
                position: 'relative'
              }}>
                {/* Reader header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isProcessing ? '#f59e0b' : '#10b981', boxShadow: isProcessing ? '0 0 8px #f59e0b' : '0 0 8px #10b981', transition: 'all 0.3s' }}></div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#334155' }}></div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#334155' }}></div>
                  </div>
                  <Wifi size={16} color={isProcessing ? '#f59e0b' : '#10b981'} style={{ transition: 'color 0.3s' }} />
                </div>

                {/* Display Screen */}
                <div style={{
                  background: isProcessing ? 'linear-gradient(135deg, #1e3a5f 0%, #1e4080 100%)' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                  borderRadius: '10px',
                  padding: '16px',
                  marginBottom: '20px',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.4s'
                }}>
                  {isProcessing ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ color: '#f59e0b', marginBottom: '8px' }} />
                      <p style={{ margin: 0, color: '#f59e0b', fontSize: '13px', fontWeight: '600', letterSpacing: '1px' }}>PROCESSING...</p>
                      <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '11px' }}>Please wait</p>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 4px', color: '#94a3b8', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase' }}>Amount Due</p>
                      <p style={{ margin: 0, color: '#f8fafc', fontSize: '24px', fontWeight: '900', letterSpacing: '1px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <span style={{ width: '40px', textAlign: 'right', marginRight: '8px' }}>{currencySymbol}</span>
                        <span style={{ textAlign: 'left' }}>{finalAmount.toFixed(2)}</span>
                      </p>
                      <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '11px' }}>SWIPE / TAP / INSERT</p>
                    </>
                  )}
                </div>

                {/* Card Slot */}
                <div style={{
                  background: '#0f172a',
                  height: '8px',
                  borderRadius: '4px',
                  position: 'relative',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  {isProcessing && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, height: '100%', width: '40%',
                      background: 'linear-gradient(90deg, transparent, #f59e0b, transparent)',
                      animation: 'slideCard 0.8s ease-in-out infinite'
                    }}></div>
                  )}
                </div>

                <p style={{ margin: 0, color: '#475569', fontSize: '10px', textAlign: 'center', letterSpacing: '0.5px' }}>
                  POS TERMINAL
                </p>
              </div>
            </div>

            {/* Payment Amount */}
            <div style={{ textAlign: 'center', marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total to Charge</p>
              <p style={{ margin: 0, fontSize: '28px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: '45px', textAlign: 'right', paddingRight: '10px' }}>{currencySymbol}</span>
                <span style={{ textAlign: 'left' }}>{finalAmount.toFixed(2)}</span>
              </p>
            </div>



            {/* Swipe Button */}
            <Button
              className="w-100"
              onClick={handleSimulateSwipe}
              disabled={isProcessing}
              style={{
                background: isProcessing
                  ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  : 'linear-gradient(135deg, #604BE8 0%, #4f3cc9 100%)',
                border: 'none',
                padding: '16px',
                borderRadius: '14px',
                fontWeight: '800',
                fontSize: '16px',
                letterSpacing: '0.5px',
                boxShadow: isProcessing ? 'none' : '0 6px 20px rgba(96, 75, 232, 0.35)',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
            >
              {isProcessing ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  Reading Card...
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  Swipe / Tap Card
                </>
              )}
            </Button>

            <p style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#94a3b8', marginBottom: 0 }}>
              Present card to the terminal to complete payment
            </p>
          </>
        )}
      </Modal.Body>

      <style>{`
        @keyframes slideCard {
          0% { left: -40%; }
          100% { left: 100%; }
        }
        .payment-success-icon {
          animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Modal>
  );
};

export default CardPaymentModal;
